import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Tooltip,
  Modal,
  Input,
  InputNumber,
  Checkbox,
  Divider,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  WarningOutlined,
  DownloadOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import { timesheetService } from '@iso/services';
import {
  EMPLOYMENT_TYPE_COLORS,
  TIMESHEET_STATUS,
  TIMESHEET_FILTERS,
} from '@iso/constants/timesheets';

const PERIOD_DAYS = 7;

// Keep every column header on one line (so the day + date never wrap
// inconsistently) and replace antd's gradient fixed-column shadow with a clean
// 1px divider before the Total group.
const TableWrap = styled.div`
  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
  .ant-table-cell-fix-left-last::after,
  .ant-table-cell-fix-right-first::after {
    box-shadow: none !important;
  }
  .ant-table-thead > tr > th.ant-table-cell-fix-right-first,
  .ant-table-tbody > tr > td.ant-table-cell-fix-right-first {
    border-left: 1px solid #eaecf0;
  }
`;

const Timesheets = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  // Approving payroll is owner/admin-only (mirrors the backend gate); hide the
  // approve controls for everyone else.
  const activePermission = useSelector((state) => state.permission.activePermission);
  const canApprove = ['owner', 'admin'].includes(activePermission);

  const [periodStart, setPeriodStart] = useState(moment().startOf('isoWeek'));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  // Cap the table body height so it scrolls internally (one row per worker) and
  // the summary tally cards below stay on-screen without scrolling the page.
  // Measured from where the table starts down to the viewport, minus room for
  // the summary cards + the table's own sticky header.
  const tableWrapRef = useRef(null);
  const [tableY, setTableY] = useState(360);
  useLayoutEffect(() => {
    const recompute = () => {
      const el = tableWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const reserve = 200; // summary cards + margins + table header
      setTableY(Math.max(200, Math.round(window.innerHeight - top - reserve)));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  const periodStartStr = periodStart.format('YYYY-MM-DD');
  const periodEnd = periodStart.clone().add(PERIOD_DAYS - 1, 'days');

  const dates = useMemo(
    () =>
      Array.from({ length: PERIOD_DAYS }, (_, i) =>
        periodStart.clone().add(i, 'days').format('YYYY-MM-DD')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodStartStr]
  );

  const loadData = () => {
    if (!activePropertyId) return;
    setLoading(true);
    timesheetService
      .getList(activePropertyId, periodStartStr)
      .then((res) => {
        setRows(res?.rows || []);
        setSummary(res?.summary || {});
      })
      .catch(() => message.error('Could not load timesheets'))
      .finally(() => setLoading(false));
  };

  useEffect(loadData, [activePropertyId, periodStartStr]);

  const shiftPeriod = (deltaWeeks) =>
    setPeriodStart(periodStart.clone().add(deltaWeeks * PERIOD_DAYS, 'days'));

  // Summary cards. Headcount / hours / overtime / cost all come from the
  // backend summary (cost uses each worker's configured pay rate + OT, and is
  // only present for owner/admin — see estimatedCost below).
  const headcount = summary.headcount ?? rows.length;
  const totalHours = summary.total_hours ?? rows.reduce((s, r) => s + (r.total || 0), 0);
  const totalOvertime = summary.total_overtime ?? rows.reduce((s, r) => s + (r.overtime || 0), 0);
  // Cost comes from the backend, using each worker's configured pay rate + OT
  // (workers without a rate contribute 0 — set rates under Team Schedule → Pay & OT).
  const estimatedCost = summary.total_cost ?? 0;
  const missingBreaks = summary.missing_break_count ?? 0;
  const conflicts = summary.conflicts ?? 0;

  // Review-and-approve modal. Rather than approving silently, the Review button
  // opens a modal that shows the exact pay period, the per-day worked hours the
  // manager is signing off on, and lets them override overtime / add notes and
  // explicitly confirm attendance before approval.
  const [reviewRecord, setReviewRecord] = useState(null);
  const [reviewOt, setReviewOt] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [approving, setApproving] = useState(false);

  const openReview = (record) => {
    setReviewRecord(record);
    setReviewOt(record.overtime || 0);
    setReviewNotes('');
    setReviewConfirmed(false);
  };

  const closeReview = () => {
    if (approving) return;
    setReviewRecord(null);
  };

  const submitReview = () => {
    if (!reviewRecord) return;
    setApproving(true);
    timesheetService
      .approve({
        property_id: activePropertyId,
        user_id: reviewRecord.user_id,
        period_start: periodStartStr,
        overtime_hours: reviewOt,
        notes: reviewNotes || null,
      })
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(`${reviewRecord.name}'s timesheet approved`);
          setReviewRecord(null);
          loadData();
        } else {
          message.error('Could not approve');
        }
      })
      .catch(() => message.error('Could not approve'))
      .finally(() => setApproving(false));
  };

  const handleApproveAll = () => {
    timesheetService
      .approveAll({ property_id: activePropertyId, period_start: periodStartStr })
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(`Approved ${res.approved} timesheet(s)`);
          loadData();
        } else {
          message.error('Could not approve all');
        }
      })
      .catch(() => message.error('Could not approve all'));
  };

  const filteredRows = rows.filter((row) => {
    if (filter === 'pending') return row.status !== 'approved';
    if (filter === 'approved') return row.status === 'approved';
    if (filter === 'conflicts') return row.has_conflict;
    return true;
  });

  const handleExport = () => {
    const header = [
      'Worker',
      'Employment Type',
      ...dates.map((d) => moment(d).format('ddd D MMM')),
      'Total',
      'Overtime',
      'Status',
    ];
    const lines = filteredRows.map((row) => [
      row.name,
      row.employment_type,
      ...dates.map((d) => row.days?.[d] || 0),
      row.total,
      row.overtime,
      row.status,
    ]);
    const csv = [header, ...lines]
      .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheets-${periodStartStr}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filterCount = (key) => {
    if (key === 'all') return summary.all || 0;
    if (key === 'pending') return summary.pending || 0;
    if (key === 'approved') return summary.approved || 0;
    if (key === 'conflicts') return summary.conflicts || 0;
    return 0;
  };

  const columns = [
    {
      title: 'Worker',
      key: 'worker',
      fixed: 'left',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.name}</div>
          <Tag color={EMPLOYMENT_TYPE_COLORS[record.employment_type] || 'default'}>
            {record.employment_type}
          </Tag>
        </div>
      ),
    },
    ...dates.map((d) => ({
      title: moment(d).format('ddd D'),
      key: d,
      align: 'center',
      width: 78,
      // Tighter header padding + nowrap (via TableWrap) so "Mon 6" / "Fri 10"
      // sit on one line consistently.
      onHeaderCell: () => ({ style: { padding: '12px 6px' } }),
      render: (_, record) => {
        const hours = record.days?.[d];
        return hours ? `${hours}h` : <span style={{ color: '#bbb' }}>—</span>;
      },
    })),
    {
      title: 'Total',
      key: 'total',
      align: 'center',
      fixed: 'right',
      width: 80,
      render: (_, record) => <strong>{record.total}h</strong>,
    },
    {
      title: 'OT',
      key: 'ot',
      align: 'center',
      fixed: 'right',
      width: 70,
      render: (_, record) =>
        record.overtime ? (
          <span style={{ color: '#fa8c16' }}>{record.overtime}h</span>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      fixed: 'right',
      width: 130,
      render: (_, record) => {
        const meta = TIMESHEET_STATUS[record.status] || {};
        return (
          <Space>
            <Tag color={meta.color}>{meta.label}</Tag>
            {record.has_conflict ? (
              <Tooltip title="Unresolved time entry (e.g. not clocked out)">
                <WarningOutlined style={{ color: '#cf1322' }} />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 110,
      render: (_, record) =>
        record.status === 'approved' || !canApprove ? null : (
          <Button
            size="small"
            icon={<CheckOutlined style={{ color: '#067647' }} />}
            onClick={() => openReview(record)}
          >
            Review
          </Button>
        ),
    },
  ];

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontWeight: 700, fontSize: 26 }}>Timesheets</h1>
        <Space>
          {canApprove ? <Button onClick={handleApproveAll}>Approve All</Button> : null}
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            Export for Payroll
          </Button>
        </Space>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => shiftPeriod(-1)} />
          <span style={{ fontWeight: 500 }}>
            {periodStart.format('D MMM')} — {periodEnd.format('D MMM YYYY')} (Weekly)
          </span>
          <Button icon={<RightOutlined />} onClick={() => shiftPeriod(1)} />
          <Button onClick={() => setPeriodStart(moment().startOf('isoWeek'))}>This week</Button>
        </Space>
        <Space>
          {TIMESHEET_FILTERS.map((f) => (
            <Button
              key={f.key}
              type={filter === f.key ? 'primary' : 'default'}
              onClick={() => setFilter(f.key)}
            >
              {f.label} ({filterCount(f.key)})
            </Button>
          ))}
        </Space>
      </div>

      <Spin spinning={loading}>
        <TableWrap ref={tableWrapRef}>
          <Table
            rowKey="user_id"
            columns={columns}
            dataSource={filteredRows}
            pagination={false}
            scroll={{ x: 'max-content', y: tableY }}
            locale={{ emptyText: 'No timesheets for this week' }}
          />
        </TableWrap>
      </Spin>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
        <SummaryCard label="Total Headcount" value={headcount} />
        <SummaryCard label="Total Hours" value={`${round1(totalHours)}h`} />
        <SummaryCard label="Total Overtime" value={`${round1(totalOvertime)}h`} valueColor="#fa8c16" />
        <SummaryCard label="Estimated Cost" value={`$${Math.round(estimatedCost).toLocaleString()}`} />
      </div>

      {/* Attention Required */}
      {missingBreaks > 0 || conflicts > 0 ? (
        <div style={{ marginTop: 16, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#b45309', marginBottom: 8 }}>
            <ExclamationCircleOutlined /> Attention Required
          </div>
          <ul style={{ margin: 0, paddingLeft: 22, color: '#7c5312' }}>
            {missingBreaks > 0 ? (
              <li>
                {missingBreaks} worker{missingBreaks === 1 ? '' : 's'} missing break records
              </li>
            ) : null}
            {conflicts > 0 ? (
              <li>
                {conflicts} unresolved time {conflicts === 1 ? 'entry' : 'entries'} (not clocked out)
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <ReviewModal
        record={reviewRecord}
        dates={dates}
        periodStart={periodStart}
        periodEnd={periodEnd}
        overtime={reviewOt}
        onOvertimeChange={setReviewOt}
        notes={reviewNotes}
        onNotesChange={setReviewNotes}
        confirmed={reviewConfirmed}
        onConfirmedChange={setReviewConfirmed}
        approving={approving}
        onCancel={closeReview}
        onApprove={submitReview}
      />
    </LayoutWrapper>
  );
};

// Review & approve modal — shows the manager the exact period and per-day hours
// they are signing off on, plus attendance summary, an OT override and notes.
const ReviewModal = ({
  record,
  dates,
  periodStart,
  periodEnd,
  overtime,
  onOvertimeChange,
  notes,
  onNotesChange,
  confirmed,
  onConfirmedChange,
  approving,
  onCancel,
  onApprove,
}) => {
  const daysWorked = record ? dates.filter((d) => (record.days?.[d] || 0) > 0).length : 0;
  const total = record ? record.total || 0 : 0;

  return (
    <Modal
      title="Review & approve timesheet"
      visible={!!record}
      onCancel={onCancel}
      maskClosable={!approving}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={approving}>
          Cancel
        </Button>,
        <Button
          key="approve"
          type="primary"
          loading={approving}
          disabled={!confirmed}
          onClick={onApprove}
        >
          Approve
        </Button>,
      ]}
    >
      {record ? (
        <div>
          {/* Who + which period is being approved */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{record.name}</div>
            <Tag color={EMPLOYMENT_TYPE_COLORS[record.employment_type] || 'default'}>
              {record.employment_type}
            </Tag>
          </div>
          <div
            style={{
              background: '#f9fafb',
              border: '1px solid #eaecf0',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
            }}
          >
            <div style={{ color: '#667085', fontSize: 12, marginBottom: 2 }}>
              Pay period you are approving
            </div>
            <div style={{ fontWeight: 600 }}>
              {periodStart.format('ddd D MMM')} — {periodEnd.format('ddd D MMM YYYY')}
            </div>
          </div>

          {/* Per-day worked / attendance breakdown */}
          <div style={{ color: '#667085', fontSize: 13, marginBottom: 8 }}>
            Hours worked ({daysWorked} of {dates.length} days)
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${dates.length}, 1fr)`,
              gap: 6,
              marginBottom: 12,
            }}
          >
            {dates.map((d) => {
              const hrs = record.days?.[d] || 0;
              return (
                <div
                  key={d}
                  style={{
                    textAlign: 'center',
                    padding: '6px 2px',
                    borderRadius: 6,
                    background: hrs > 0 ? '#ecfdf3' : '#f9fafb',
                    border: `1px solid ${hrs > 0 ? '#abefc6' : '#eaecf0'}`,
                  }}
                >
                  <div style={{ fontSize: 11, color: '#667085' }}>
                    {moment(d).format('ddd D')}
                  </div>
                  <div style={{ fontWeight: 600, color: hrs > 0 ? '#067647' : '#bbb' }}>
                    {hrs > 0 ? `${hrs}h` : '—'}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: '#667085' }}>Total hours</span>
            <strong>{round1(total)}h</strong>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Overtime override */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              Overtime hours{' '}
              <Tooltip title="Auto-calculated from pay & OT settings. Adjust if you need to override before approving.">
                <span style={{ color: '#667085', fontSize: 12 }}>(override)</span>
              </Tooltip>
            </div>
            <InputNumber
              min={0}
              max={total}
              step={0.25}
              value={overtime}
              onChange={(v) => onOvertimeChange(v || 0)}
              style={{ width: '100%' }}
              disabled={approving}
            />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>Notes (optional)</div>
            <Input.TextArea
              rows={2}
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="e.g. adjusted for approved leave, missing break added…"
              disabled={approving}
            />
          </div>

          {/* Explicit attendance confirmation */}
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            disabled={approving}
          >
            I have reviewed the attendance and hours above and confirm they are correct.
          </Checkbox>
        </div>
      ) : null}
    </Modal>
  );
};

const round1 = (n) => Math.round((n || 0) * 10) / 10;

const SummaryCard = ({ label, value, valueColor }) => (
  <div style={{ background: '#fff', border: '1px solid #eaecf0', borderRadius: 12, padding: '18px 20px' }}>
    <div style={{ color: '#667085', fontSize: 13, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color: valueColor || '#101828' }}>{value}</div>
  </div>
);

export default Timesheets;
